'use client';

import * as React from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { AUTH_EXPIRED_EVENT, type AuthExpiredDetail } from './authEvents';
import { bootstrapSession } from './fetchWithAuth';
import { clearTokens, getTokens, type AuthTokens } from './tokenStore';
import {
  getProfile,
  loginUser,
  logoutUser,
  registerUser,
  type LoginRequest,
  type RegistrationRequest,
} from './authApi';
import {
  apiUserToProfile,
  clearCachedProfile,
  setCachedProfile,
  type UserProfile,
  EMPTY_PROFILE,
} from '@/lib/profile/profileStore';

type AuthState =
  | { status: 'anonymous' }
  | { status: 'authenticated'; tokens: AuthTokens; user: UserProfile };

type AuthContextValue = {
  state: AuthState;
  user: UserProfile | null;
  login: (payload: LoginRequest) => Promise<void>;
  register: (payload: RegistrationRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshFromStorage: () => void;
  refreshProfile: () => Promise<void>;
  establishSession: () => Promise<void>;
  setUser: (user: UserProfile) => void;
};

const AuthContext = React.createContext<AuthContextValue | null>(null);

const PUBLIC_PATHS = new Set([
  '/login',
  '/registration',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
]);

async function loadProfileSafe(): Promise<UserProfile> {
  try {
    const user = await getProfile();
    return apiUserToProfile(user);
  } catch {
    return { ...EMPTY_PROFILE };
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const [state, setState] = React.useState<AuthState>({ status: 'anonymous' });

  const applyAuthenticated = React.useCallback((tokens: AuthTokens, user: UserProfile) => {
    setCachedProfile(user);
    setState({ status: 'authenticated', tokens, user });
  }, []);

  const syncSession = React.useCallback(async () => {
    const tokens = await bootstrapSession();
    if (tokens) {
      const user = await loadProfileSafe();
      applyAuthenticated(tokens, user);
    } else {
      clearCachedProfile();
      setState({ status: 'anonymous' });
    }
  }, [applyAuthenticated]);

  const refreshFromStorage = React.useCallback(() => {
    void syncSession();
  }, [syncSession]);

  const refreshProfile = React.useCallback(async () => {
    if (state.status !== 'authenticated') return;
    const user = await loadProfileSafe();
    setCachedProfile(user);
    setState((prev) =>
      prev.status === 'authenticated' ? { ...prev, user } : prev,
    );
  }, [state.status]);

  const establishSession = React.useCallback(async () => {
    const tokens = getTokens();
    if (!tokens) return;
    const user = await loadProfileSafe();
    applyAuthenticated(tokens, user);
  }, [applyAuthenticated]);

  const setUser = React.useCallback((user: UserProfile) => {
    setCachedProfile(user);
    setState((prev) =>
      prev.status === 'authenticated' ? { ...prev, user } : prev,
    );
  }, []);

  React.useEffect(() => {
    void syncSession();

    const onExpired = (e: Event) => {
      clearCachedProfile();
      setState({ status: 'anonymous' });
      const redirect = (e as CustomEvent<AuthExpiredDetail>).detail?.redirect === true;
      if (!redirect) return;
      const path = pathname ?? '/';
      if (PUBLIC_PATHS.has(path)) return;
      router.push('/login?expired=1');
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key && e.key !== 'lk_meet_auth') return;
      void syncSession();
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
      window.removeEventListener('storage', onStorage);
    };
  }, [syncSession, pathname, router]);

  const login = React.useCallback(
    async (payload: LoginRequest) => {
      const { tokens, user } = await loginUser(payload);
      const full = await loadProfileSafe();
      applyAuthenticated(tokens, {
        username: full.username || user.username,
        email: full.email,
        fullName: full.fullName || user.full_name,
        avatar: full.avatar || user.avatar,
      });
    },
    [applyAuthenticated],
  );

  const register = React.useCallback(async (payload: RegistrationRequest) => {
    await registerUser(payload);
  }, []);

  const logout = React.useCallback(async () => {
    const tokens = getTokens();
    if (!tokens) {
      clearTokens();
      clearCachedProfile();
      setState({ status: 'anonymous' });
      return;
    }
    try {
      await logoutUser(tokens.refreshToken);
    } finally {
      clearTokens();
      clearCachedProfile();
      setState({ status: 'anonymous' });
    }
  }, []);

  const user = state.status === 'authenticated' ? state.user : null;

  const value: AuthContextValue = React.useMemo(
    () => ({
      state,
      user,
      login,
      register,
      logout,
      refreshFromStorage,
      refreshProfile,
      establishSession,
      setUser,
    }),
    [state, user, login, register, logout, refreshFromStorage, refreshProfile, establishSession, setUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
